import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import {
  ArrowLeft, RefreshCw, Send, Mail, Activity, Users,
  TrendingUp, Clock, Sparkles, Zap, AlertCircle, ThumbsUp, ThumbsDown,
  Globe, Lightbulb,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const TOOL_LABELS = {
  report: 'Report Builder',
  parent: 'Parent Summary',
  glp: 'GLP Support Guide',
  activity: 'Activity Generator',
  style: 'Style Extractor',
  'session-bridge': 'Session-to-Home Bridge',
};

const TOOL_COLORS = {
  report: '#7c6f9c',
  parent: '#059669',
  glp: '#d97706',
  activity: '#dc2626',
  style: '#2563eb',
  'session-bridge': '#9333ea',
};

export default function LiteAdmin({ onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [windowHours, setWindowHours] = useState(24);
  const [stats, setStats] = useState(null);
  const [timeseries, setTimeseries] = useState([]);
  const [digestHistory, setDigestHistory] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [sendingDigest, setSendingDigest] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchAll = async (hrs = windowHours) => {
    setLoading(true);
    try {
      const [s, ts, dh, wl] = await Promise.all([
        axios.get(`${API}/lite/admin/stats?hours=${hrs}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/lite/admin/stats/timeseries?days=14`, { headers: getAuthHeaders() }),
        axios.get(`${API}/lite/admin/digest/history?limit=14`, { headers: getAuthHeaders() }),
        axios.get(`${API}/lite/admin/wishlist?limit=50`, { headers: getAuthHeaders() }),
      ]);
      setStats(s.data.stats);
      setTimeseries(ts.data.rows || []);
      setDigestHistory(dh.data.rows || []);
      setWishlist(wl.data.rows || []);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Super admin access required');
        navigate('/');
      } else {
        toast.error('Failed to load Lite analytics');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll(windowHours);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowHours]);

  const handleSendDigestNow = async () => {
    if (!window.confirm('Send the Lite usage digest email to all super admins right now?')) return;
    setSendingDigest(true);
    try {
      const { data } = await axios.post(
        `${API}/lite/admin/digest/send-now?force=true&hours=24`,
        null,
        { headers: getAuthHeaders() },
      );
      if (data.sent) {
        toast.success(`Digest sent. ${data.stats?.total_generations || 0} generations in window.`);
        fetchAll(windowHours);
      } else {
        toast.error(`Digest attempted but not sent. ${data.reason || ''}`);
      }
    } catch (err) {
      toast.error('Failed to send digest');
    } finally {
      setSendingDigest(false);
    }
  };

  // Build 14-day chart data
  const buildChartDays = () => {
    const dayMap = {};
    timeseries.forEach((r) => {
      if (!dayMap[r.date]) dayMap[r.date] = { date: r.date, total: 0, byTool: {} };
      dayMap[r.date].total += r.count;
      dayMap[r.date].byTool[r.tool] = (dayMap[r.date].byTool[r.tool] || 0) + r.count;
    });
    // Fill missing days with zeroes (last 14)
    const days = [];
    const today = new Date();
    for (let i = 13; i >= 0; i--) {
      const d = new Date(today);
      d.setUTCDate(d.getUTCDate() - i);
      const key = d.toISOString().slice(0, 10);
      days.push(dayMap[key] || { date: key, total: 0, byTool: {} });
    }
    return days;
  };

  const chartDays = buildChartDays();
  const maxDay = Math.max(1, ...chartDays.map((d) => d.total));

  return (
    <div className="min-h-screen bg-stone-50" data-testid="lite-admin-page">
      <header className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
              data-testid="lite-admin-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <div className="text-xs font-semibold tracking-wide text-violet-700">
                PLAYFIRST LITE · ADMIN
              </div>
              <h1 className="text-xl font-bold text-stone-900">Usage & conversions</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchAll(windowHours)}
              disabled={loading}
              data-testid="lite-admin-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              size="sm"
              onClick={handleSendDigestNow}
              disabled={sendingDigest}
              data-testid="lite-admin-send-digest-btn"
              className="bg-violet-600 hover:bg-violet-700"
            >
              <Send className="w-4 h-4 mr-2" />
              {sendingDigest ? 'Sending…' : 'Send digest now'}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Privacy banner */}
        <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-sm">
          <Sparkles className="w-4 h-4 text-emerald-700 mt-0.5 flex-shrink-0" />
          <div className="text-emerald-900">
            <strong>Privacy-safe analytics.</strong> Counts only. No prompts, outputs, or plaintext
            emails are ever stored or shown here. Users are counted by hashed IP.
          </div>
        </div>

        {/* Window selector */}
        <div className="flex items-center gap-2 text-sm">
          <span className="text-stone-600">Window:</span>
          {[24, 72, 168, 720].map((h) => (
            <button
              key={h}
              onClick={() => setWindowHours(h)}
              data-testid={`lite-admin-window-${h}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                windowHours === h
                  ? 'bg-violet-600 text-white'
                  : 'bg-white border border-stone-200 text-stone-700 hover:border-stone-300'
              }`}
            >
              {h === 24 ? 'Last 24h' : h === 72 ? 'Last 3 days' : h === 168 ? 'Last 7 days' : 'Last 30 days'}
            </button>
          ))}
        </div>

        {loading && !stats ? (
          <div className="text-center py-12 text-stone-500">Loading…</div>
        ) : !stats ? (
          <div className="text-center py-12 text-stone-500">No data available</div>
        ) : (
          <>
            {/* KPI cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard
                icon={<Activity className="w-4 h-4" />}
                label="Generations"
                value={stats.total_generations}
                color="violet"
                testId="kpi-generations"
              />
              <StatCard
                icon={<Users className="w-4 h-4" />}
                label="Unique users"
                value={`~${stats.distinct_users_approx}`}
                subtext="by hashed IP"
                color="blue"
                testId="kpi-users"
              />
              <StatCard
                icon={<Mail className="w-4 h-4" />}
                label="Email captures"
                value={stats.email_captures}
                color="amber"
                testId="kpi-email-captures"
              />
              <StatCard
                icon={<Zap className="w-4 h-4" />}
                label="New Lite Pro"
                value={stats.lite_pro_new}
                color="emerald"
                testId="kpi-lite-pro"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <StatCard
                icon={<TrendingUp className="w-4 h-4" />}
                label="Waitlist signups"
                value={stats.waitlist_new}
                color="blue"
                testId="kpi-waitlist"
              />
              <StatCard
                icon={<Clock className="w-4 h-4" />}
                label="Peak hour (UTC)"
                value={
                  stats.peak_hour_utc !== null && stats.peak_hour_utc !== undefined
                    ? `${String(stats.peak_hour_utc).padStart(2, '0')}:00`
                    : '—'
                }
                subtext={
                  stats.peak_hour_utc !== null && stats.peak_hour_utc !== undefined
                    ? `= ${String((stats.peak_hour_utc + 2) % 24).padStart(2, '0')}:00 SAST`
                    : undefined
                }
                color="stone"
                testId="kpi-peak-hour"
              />
              <StatCard
                icon={<Sparkles className="w-4 h-4" />}
                label="Tokens used"
                value={stats.total_tokens?.toLocaleString() || 0}
                color="stone"
                testId="kpi-tokens"
              />
            </div>

            {/* Feedback card */}
            {(stats.feedback_up > 0 || stats.feedback_down > 0) && (
              <Card className="p-5" data-testid="feedback-card">
                <h2 className="text-sm font-semibold text-stone-900 mb-4">Tool feedback</h2>
                <div className="flex items-center gap-6 mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center">
                      <ThumbsUp className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="text-xl font-bold text-stone-900">{stats.feedback_up}</div>
                      <div className="text-[11px] text-stone-500">positive</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-red-50 text-red-700 flex items-center justify-center">
                      <ThumbsDown className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="text-xl font-bold text-stone-900">{stats.feedback_down}</div>
                      <div className="text-[11px] text-stone-500">needs work</div>
                    </div>
                  </div>
                  <div className="flex-1" />
                  <div className="text-right">
                    <div className="text-2xl font-bold text-stone-900">
                      {Math.round(
                        (stats.feedback_up / Math.max(1, stats.feedback_up + stats.feedback_down)) * 100,
                      )}%
                    </div>
                    <div className="text-[11px] text-stone-500">satisfaction</div>
                  </div>
                </div>

                {stats.feedback_by_tool?.length > 0 && (
                  <div className="space-y-2 pt-3 border-t border-stone-100">
                    {stats.feedback_by_tool.map((t) => {
                      const total = (t.up || 0) + (t.down || 0);
                      const upPct = total > 0 ? (t.up / total) * 100 : 0;
                      return (
                        <div key={t.tool} className="text-sm" data-testid={`feedback-tool-${t.tool}`}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-stone-800">
                              {TOOL_LABELS[t.tool] || t.tool}
                            </span>
                            <span className="text-stone-500">
                              👍 {t.up || 0} · 👎 {t.down || 0}
                            </span>
                          </div>
                          <div className="w-full bg-red-100 rounded-full h-1.5 overflow-hidden">
                            <div
                              className="h-1.5 bg-emerald-500 rounded-full transition-all"
                              style={{ width: `${upPct}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card>
            )}

            {/* Per-tool breakdown */}
            <Card className="p-5">
              <h2 className="text-sm font-semibold text-stone-900 mb-4">By tool</h2>
              {!stats.per_tool?.length ? (
                <div className="text-center py-6 text-stone-400 text-sm">
                  No generations in this window
                </div>
              ) : (
                <div className="space-y-3">
                  {stats.per_tool.map((t) => {
                    const pct =
                      stats.total_generations > 0
                        ? Math.round((t.count / stats.total_generations) * 100)
                        : 0;
                    const color = TOOL_COLORS[t.tool] || '#78716c';
                    return (
                      <div key={t.tool} data-testid={`tool-row-${t.tool}`}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="font-medium text-stone-800">
                            {TOOL_LABELS[t.tool] || t.tool}
                          </span>
                          <span className="text-stone-500">
                            <strong className="text-stone-900">{t.count}</strong>
                            {' · '}
                            {t.distinct_ips_count} users
                          </span>
                        </div>
                        <div className="w-full bg-stone-100 rounded-full h-2 overflow-hidden">
                          <div
                            className="h-2 rounded-full transition-all"
                            style={{ width: `${pct}%`, background: color }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>

            {/* Visits */}
            {stats.pageviews && stats.pageviews.total_visits > 0 && (
              <Card className="p-5" data-testid="visits-card">
                <h2 className="text-sm font-semibold text-stone-900 mb-4 flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  Visits in window
                </h2>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <StatCard
                    icon={<Globe className="w-4 h-4" />}
                    label="Total visits"
                    value={stats.pageviews.total_visits}
                    color="stone"
                    testId="kpi-pv-total"
                  />
                  <StatCard
                    icon={<Users className="w-4 h-4" />}
                    label="Unique"
                    subtext="daily-rotated salt"
                    value={stats.pageviews.unique_daily_visitors}
                    color="blue"
                    testId="kpi-pv-unique"
                  />
                  <StatCard
                    icon={<TrendingUp className="w-4 h-4" />}
                    label="Visit→generate"
                    value={`${
                      stats.pageviews.total_visits > 0
                        ? Math.round((stats.total_generations / stats.pageviews.total_visits) * 100)
                        : 0
                    }%`}
                    color="emerald"
                    testId="kpi-pv-conversion"
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2">Top paths</h3>
                    <div className="space-y-1.5">
                      {(stats.pageviews.top_paths || []).slice(0, 6).map((p, i) => (
                        <div key={i} className="flex items-center justify-between text-sm py-1 border-b border-stone-100 last:border-0">
                          <span className="font-mono text-xs text-stone-700">{p.path}</span>
                          <span className="font-semibold text-stone-900">{p.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2">Top sources</h3>
                    <div className="space-y-1.5">
                      {(stats.pageviews.top_referrers || []).slice(0, 6).map((r, i) => (
                        <div key={i} className="flex items-center justify-between text-sm py-1 border-b border-stone-100 last:border-0">
                          <span className="text-stone-700">{r.domain}</span>
                          <span className="font-semibold text-stone-900">{r.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {/* Wishlist */}
            {wishlist.length > 0 && (
              <Card className="p-5" data-testid="wishlist-card">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-sm font-semibold text-stone-900 flex items-center gap-2">
                    <Lightbulb className="w-4 h-4 text-amber-600" />
                    Wishlist notes
                  </h2>
                  <span className="text-xs text-stone-500">{wishlist.length} total · 90-day TTL</span>
                </div>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {wishlist.map((w, i) => (
                    <div
                      key={i}
                      className="rounded-xl border border-stone-100 bg-stone-50/50 p-3 text-sm"
                      data-testid={`wishlist-row-${i}`}
                    >
                      <div className="text-stone-800 leading-relaxed whitespace-pre-wrap">
                        {w.message}
                      </div>
                      <div className="flex items-center justify-between mt-2 pt-2 border-t border-stone-100">
                        <span className="text-xs text-stone-500">
                          {w.timestamp ? new Date(w.timestamp).toLocaleString() : '—'}
                          {w.page ? ` · ${w.page}` : ''}
                        </span>
                        {w.email_plain ? (
                          <a
                            href={`mailto:${w.email_plain}?subject=Re: your PlayFirst wishlist note`}
                            className="text-xs text-amber-700 hover:underline"
                            data-testid={`wishlist-reply-${i}`}
                          >
                            Reply: {w.email_plain}
                          </a>
                        ) : (
                          <span className="text-xs text-stone-400">Anonymous</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* 14-day chart */}
            <Card className="p-5">
              <h2 className="text-sm font-semibold text-stone-900 mb-4">Last 14 days</h2>
              <div className="flex items-end gap-1 h-32">
                {chartDays.map((d) => {
                  const h = Math.max(2, (d.total / maxDay) * 100);
                  return (
                    <div
                      key={d.date}
                      className="flex-1 flex flex-col items-center gap-1 group cursor-default"
                      title={`${d.date}: ${d.total} generation${d.total === 1 ? '' : 's'}`}
                    >
                      <div
                        className="w-full bg-violet-500 rounded-t-sm transition-all group-hover:bg-violet-700"
                        style={{ height: `${h}%` }}
                        data-testid={`chart-day-${d.date}`}
                      />
                      <div className="text-[10px] text-stone-500">
                        {d.date.slice(8, 10)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>

            {/* Digest history */}
            <Card className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold text-stone-900">Recent digest emails</h2>
                <span className="text-xs text-stone-500">
                  Daily at 08:00 SAST (06:00 UTC)
                </span>
              </div>
              {!digestHistory.length ? (
                <div className="text-sm text-stone-500 py-4">
                  No digests sent yet. Click "Send digest now" to fire the first one.
                </div>
              ) : (
                <div className="divide-y divide-stone-100">
                  {digestHistory.map((d, i) => (
                    <div
                      key={i}
                      className="py-2 flex items-center justify-between text-sm"
                      data-testid={`digest-row-${i}`}
                    >
                      <div>
                        <div className="font-medium text-stone-800">
                          {new Date(d.sent_at).toLocaleString()}
                        </div>
                        <div className="text-xs text-stone-500">
                          {d.stats_total} generations · {d.success ? 'Sent' : 'Failed'}
                        </div>
                      </div>
                      <div
                        className={`text-xs px-2 py-1 rounded-full ${
                          d.success
                            ? 'bg-emerald-100 text-emerald-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {d.success ? 'OK' : 'Error'}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </>
        )}
      </main>
    </div>
  );
}

function StatCard({ icon, label, value, subtext, color, testId }) {
  const colorMap = {
    violet: 'from-violet-50 to-violet-100 border-violet-200 text-violet-700',
    blue: 'from-blue-50 to-blue-100 border-blue-200 text-blue-700',
    amber: 'from-amber-50 to-amber-100 border-amber-200 text-amber-800',
    emerald: 'from-emerald-50 to-emerald-100 border-emerald-200 text-emerald-800',
    stone: 'from-stone-50 to-stone-100 border-stone-200 text-stone-700',
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-gradient-to-br ${colorMap[color] || colorMap.stone} border rounded-xl p-4`}
      data-testid={testId}
    >
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide">
        {icon}
        {label}
      </div>
      <div className="text-2xl font-bold text-stone-900 mt-1">{value}</div>
      {subtext && <div className="text-[11px] text-stone-500 mt-0.5">{subtext}</div>}
    </motion.div>
  );
}
